import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import history from '../../utils/history'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import {
  EditModuleType,
  CreateScreenType,
  EditScreenType,
  RemoveScreenType,
} from './types'

function* fetchModule(payload: { id: string }): SagaIterator {
  try {
    const module = yield effects.call(api.fetchModule, payload.id)
    yield effects.put(actions.fetchModuleSuccess(module.data))
  } catch (error) {
    console.warn('Cannot fetch module', error)
    yield effects.put(actions.fetchModuleFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchModuleScreens(payload: {
  id: string
  mid: string
}): SagaIterator {
  try {
    const { id, mid } = payload
    const module = yield effects.call(api.fetchModuleScreens, id, mid)
    yield effects.put(actions.fetchModuleScreensSuccess(module.data))
  } catch (error) {
    console.warn('Cannot fetch module screens', error)
    yield effects.put(
      actions.fetchModuleScreensFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* editModule(payload: EditModuleType): SagaIterator {
  try {
    const { id, title, position, available_at } = payload
    yield effects.call(api.editModule, id, title, position, available_at)
    yield effects.put(actions.editModuleSuccess())
    const url = window.location.pathname
    const prefix = process.env.REACT_APP_URI_PREFIX
    yield effects.call(
      [history, history.push],
      url.split('/modules/')[0]?.replace(prefix, '')
    )
  } catch (error) {
    console.warn('Cannot edit module', error)
    yield effects.put(actions.editModuleFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeModule(payload: { id: string }): SagaIterator {
  try {
    yield effects.call(api.removeModule, payload.id)
    yield effects.put(actions.removeModuleSuccess())
    const url = window.location.pathname
    const prefix = process.env.REACT_APP_URI_PREFIX
    yield effects.call(
      [history, history.push],
      url.split('/modules/')[0]?.replace(prefix, '')
    )
  } catch (error) {
    console.warn('Cannot remove module', error)
    yield effects.put(actions.removeModuleFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createScreen(payload: CreateScreenType): SagaIterator {
  try {
    const { id, mid, title, typeScreen } = payload
    yield effects.call(api.createScreen, mid, title, typeScreen)
    yield effects.put(actions.createScreenSuccess())
    yield effects.put(actions.fetchModuleScreens(id, mid))
  } catch (error) {
    console.warn('Cannot create screen', error)
    yield effects.put(actions.createScreenFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* editScreen(payload: EditScreenType): SagaIterator {
  try {
    const { id, mid, sid, title } = payload
    yield effects.call(api.editScreen, sid, title)
    yield effects.put(actions.editScreenSuccess())
    yield effects.put(actions.fetchModuleScreens(id, mid))
  } catch (error) {
    console.warn('Cannot edit screen', error)
    yield effects.put(actions.editScreenFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeScreen(payload: RemoveScreenType): SagaIterator {
  try {
    const { id, mid, sid } = payload
    yield effects.call(api.removeScreen, sid)
    yield effects.put(actions.removeScreenSuccess())
    yield effects.put(actions.fetchModuleScreens(id, mid))
  } catch (error) {
    console.warn('Cannot remove screen', error)
    yield effects.put(actions.removeScreenFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchModule(): SagaIterator {
  yield effects.takeEvery(actions.fetchModule.toString(), fetchModule)
}

function* watchFetchModuleScreens(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchModuleScreens.toString(),
    fetchModuleScreens
  )
}

function* watchEditModule(): SagaIterator {
  yield effects.takeEvery(actions.editModule.toString(), editModule)
}

function* watchRemoveModule(): SagaIterator {
  yield effects.takeEvery(actions.removeModule.toString(), removeModule)
}

function* watchCreateScreen(): SagaIterator {
  yield effects.takeEvery(actions.createScreen.toString(), createScreen)
}

function* watchEditScreen(): SagaIterator {
  yield effects.takeEvery(actions.editScreen.toString(), editScreen)
}

function* watchRemoveScreen(): SagaIterator {
  yield effects.takeEvery(actions.removeScreen.toString(), removeScreen)
}

export default function* watchAdminEditModule(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchModule),
    effects.fork(watchEditModule),
    effects.fork(watchRemoveModule),
    effects.fork(watchFetchModuleScreens),
    effects.fork(watchCreateScreen),
    effects.fork(watchEditScreen),
    effects.fork(watchRemoveScreen),
  ])
}
